import _ from 'lodash';
import moment from 'moment';
import { TRACKING_MAX_DAYS } from '../../../constants/tracking';
import { isClientRemindMe, remindMeSort, trackingSort } from '../../../utils/adviser-client';
import { adviserFactsheetOf, qualifyingFactsheetOf } from '../../../utils/client-factsheet';
import { clientNameOf } from './clientPresentation';
import type { QueueClient } from '../../../types/client';

/**
 * Coverage-gap list states, reached from the dashboard's PolicyCategoryTiles.
 * Despite the plain names, each one lists the clients who have **no** policy in
 * that category: advDashController.connectedUserPolicies pushes a client into
 * `users` only when its per-category count is 0 — which is why V1 labels them
 * 「Without Life」/「Without C.I.」/… (clientPage.js:2228-2265).
 */
export const POLICY_GAP_STATES = ['life', 'illness', 'medical', 'saving', 'ilas'] as const;
export type PolicyGapState = (typeof POLICY_GAP_STATES)[number];

export const CLIENT_LEGACY_STATES = [
  'Connected',
  'Disconnected',
  'Inactive',
  'Pending',
  'TrackOn',
  'longPendingCases',
  'imported',
  'Long time no see',
  'remindMe',
  'dateBackBirthdayCount',
  'particular',
  'allReminder',
  'TodayAndTomorrowBirthday',
  'ClientToSend',
  'lucky',
  ...POLICY_GAP_STATES
] as const;
export type ClientLegacyState = (typeof CLIENT_LEGACY_STATES)[number];

/**
 * `state.adviserDashboard.policiesData`. Non-Pro advisers get plain zeros
 * instead of `{ count, users }` objects (advDashController's subscription
 * short-circuit), so every read has to tolerate a number here.
 */
export type PolicyGapEntry = { users?: Array<{ _id?: string }> };
export type PolicyGapData = Partial<Record<PolicyGapState, PolicyGapEntry | number>>;

const isPolicyGapState = (state: ClientLegacyState): state is PolicyGapState =>
  (POLICY_GAP_STATES as readonly string[]).includes(state);

export type ClientLegacyStateInput = {
  state: ClientLegacyState;
  clientId?: string;
  referenceDate?: moment.MomentInput;
  /** `location.preSelectedClientIds`, carried by the `particular` deep link. */
  clientIds?: string[];
};

/** `state.adviserDashboard.PastBirthdayOfUsers` — drives dateBackBirthdayCount. */
export type PastBirthdayUser = { _id?: string };

export type ClientLegacyStateRequest = ClientLegacyStateInput & {
  token: number;
};

/**
 * Label for the active list state, reusing the exact message ids V1 shows in
 * its `clientState` <select> (clientPage.js: the `hide-option` entries plus the
 * always-present Connected / All Remind me options). No new i18n keys needed.
 */
export const LEGACY_STATE_LABEL_KEYS: Record<ClientLegacyState, string> = {
  Connected: 'Connected',
  Disconnected: 'Disconnected',
  Inactive: 'Inactive',
  Pending: 'Pending',
  // V1 reaches TrackOn from a toggle button, not the clientState <select>, so
  // it has no option label there. Reuse the 跟進中 chip's existing id.
  TrackOn: 'Track Biz On',
  longPendingCases: 'long-pending-cases',
  imported: 'Imported',
  'Long time no see': 'Long time no see',
  remindMe: 'Remind me',
  dateBackBirthdayCount: 'Date Back',
  particular: 'Selected Clients',
  allReminder: 'All Remind me',
  TodayAndTomorrowBirthday: 'Birthday_Pop_Up',
  ClientToSend: 'clients-to-send',
  lucky: 'Lucky',
  life: 'Without Life',
  illness: 'Without C.I.',
  medical: 'Without Medical',
  saving: 'Without Saving',
  ilas: 'Without ILAS'
};

const recordOf = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const stringOf = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const momentInputOf = (value: unknown): moment.MomentInput | undefined => {
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) return value;
  return moment.isMoment(value) ? value : undefined;
};

export const legacyClientStateInputOf = (
  flag: string,
  clientId?: string | null,
  referenceDate?: moment.MomentInput,
  clientIds?: string[]
): ClientLegacyStateInput | null => {
  if (!CLIENT_LEGACY_STATES.includes(flag as ClientLegacyState)) return null;
  return {
    state: flag as ClientLegacyState,
    ...(clientId ? { clientId } : {}),
    ...(referenceDate !== undefined ? { referenceDate } : {}),
    ...(clientIds ? { clientIds } : {})
  };
};

/**
 * React Router v4 location payload used by the old /client page and by
 * NotificationCenterModal. Keeping this parser pure lets ClientV2 honour both
 * same-page notification clicks and deep links arriving from another page.
 */
export const legacyClientStateInputFromLocation = (
  location?: Record<string, unknown>
): ClientLegacyStateInput | null => {
  if (!location) return null;
  const state = recordOf(location.state);
  const select = stringOf(location.select);
  if (!select) return null;

  if (select === 'Connected') {
    return legacyClientStateInputOf(select, stringOf(state?.connectedId));
  }
  if (select === 'allReminder') {
    return legacyClientStateInputOf(select, stringOf(state?.remindMeId));
  }
  if (select === 'TodayAndTomorrowBirthday') {
    return legacyClientStateInputOf(select, undefined, momentInputOf(state?.BD));
  }
  if (select === 'lucky') {
    // Dashboard user.js:286-310 puts the chosen client at the TOP level of the
    // location, alongside `select` and `smartIdea`. V1's own restore reads
    // `location.state.selectedClient` and therefore never matches this link,
    // which is why its lucky list comes up empty (or shows a stale client).
    // Read both shapes so the link narrows to the client it actually names.
    const selected = recordOf(location.selectedClient) || recordOf(state?.selectedClient);
    return legacyClientStateInputOf(select, stringOf(selected?._id));
  }

  if (select === 'particular') {
    // referralRecords sends the chosen ids alongside `select` (not in `state`).
    const ids = Array.isArray(location.preSelectedClientIds)
      ? (location.preSelectedClientIds as unknown[]).filter((id): id is string => typeof id === 'string')
      : undefined;
    return legacyClientStateInputOf(select, undefined, undefined, ids);
  }
  // The rest (ClientToSend, the connection states, TrackOn, and the
  // PolicyCategoryTiles coverage links) carry nothing but `select`.
  return legacyClientStateInputOf(select);
};

// Same identity rule the rows render (clientPresentation.clientNameOf), so the
// name tiebreak below orders by what the adviser actually sees.
const byName = (left: QueueClient, right: QueueClient): number => clientNameOf(left).localeCompare(clientNameOf(right));

const connectedToAdviser = (client: QueueClient, adviserEmail?: string): boolean => {
  const detail = (client.advisorDetails || []).find(entry => !adviserEmail || entry.email === adviserEmail);
  return Boolean(detail?.connection && !client.passwordChangeKey);
};

/**
 * V1's `Disconnected` list (clientPage.js:1435) is NOT
 * `getClientConnectionStatus(client) === 'Disconnected'` — it is the literal
 * per-adviser predicate "this adviser has an advisorDetails entry whose
 * connection is false, and no password reset is in flight", which also picks up
 * inactive users that status helper would classify as Inactive/Pending. Mirror
 * the predicate, not the status. `.some` rather than V1's `.map`+push, which
 * emits a duplicate row when two adviser entries match.
 */
const disconnectedFromAdviser = (client: QueueClient, adviserEmail?: string): boolean =>
  !client.passwordChangeKey &&
  (client.advisorDetails || []).some(entry => (!adviserEmail || entry.email === adviserEmail) && !entry.connection);

/**
 * MIRROR — ClientEntry utils.js `getClientConnectionStatus`, used by V1's
 * `Pending` branch (clientPage.js:1458). Kept here rather than imported (see
 * Established Pattern #1); `connectionStatusOf` in clientPresentation.ts is the
 * same logic for the drawer pill.
 */
const isPendingClient = (client: QueueClient): boolean => {
  const disconnected = !(
    client.passwordChangeKey ||
    client.inactiveUser ||
    (client.advisorDetails || []).some(entry => entry.connection)
  );
  if (disconnected) return false;
  return client.inactiveUser ? Boolean(client.connectionRequestId) : Boolean(client.passwordChangeKey);
};

/**
 * Blast-message opt-in for the ClientToSend list. The real server field is
 * `factsheet.msgOpt` (true = will receive the message) — mirrors ClientEntry.js
 * `checked={msgOpt}` / `handleMsgOptChange`. Adviser-owned factsheet field.
 */
export const msgOptOf = (client: QueueClient, adviserEmail?: string): boolean =>
  Boolean(adviserFactsheetOf(client, adviserEmail)?.msgOpt);

export type MovePolicyTargetError = 'same-client' | 'ineligible';

/**
 * Target-selection rules for the move-policy flow, mirroring ClientEntry.js
 * (`action === 'movePolicy'` only renders its checkbox when the row is not the
 * source client AND is `inactiveUser || connected to this adviser`). Returns
 * why a target is rejected, or null when it is selectable.
 */
export const movePolicyTargetErrorOf = (
  client: QueueClient,
  sourceClientId: string | undefined,
  adviserEmail?: string
): MovePolicyTargetError | null => {
  if (sourceClientId && client._id === sourceClientId) return 'same-client';
  const isConnected = Boolean(
    (client.advisorDetails || []).find(entry => (!adviserEmail || entry.email === adviserEmail) && entry.connection)
  );
  if (!client.inactiveUser && !isConnected) return 'ineligible';
  return null;
};

const filterByClientId = (clients: QueueClient[], clientId?: string): QueueClient[] =>
  clientId ? clients.filter(client => client._id === clientId) : clients;

const birthdayKeyOf = (client: QueueClient, adviserEmail: string | undefined, now: moment.Moment): number => {
  const dob = adviserFactsheetOf(client, adviserEmail)?.dob;
  if (!dob) return Number.POSITIVE_INFINITY;
  const parsed = moment.parseZone(dob);
  if (!parsed.isValid()) return Number.POSITIVE_INFINITY;
  const nextBirthday = now.clone().month(parsed.month()).date(parsed.date()).startOf('day');
  if (nextBirthday.isBefore(now.clone().startOf('day'))) nextBirthday.add(1, 'year');
  return nextBirthday.valueOf();
};

/**
 * Applies the three legacy list states still emitted by notifications and
 * top-navigation deep links. This helper only transforms the list handed to
 * it; page statistics, TodayQueue, tab counts and sidebar options continue to
 * derive from the complete clientData array in component.tsx.
 */
export const filterClientsByLegacyState = (
  clients: QueueClient[],
  request: ClientLegacyStateInput | null,
  input: {
    adviserEmail?: string;
    now?: moment.MomentInput;
    policiesData?: PolicyGapData;
    pastBirthdayUsers?: PastBirthdayUser[];
  } = {}
): QueueClient[] => {
  if (!request) return clients;

  if (isPolicyGapState(request.state)) {
    const entry = input.policiesData?.[request.state];
    const users = entry && typeof entry === 'object' ? entry.users : undefined;
    // V1 guards the same way (`if (mapData && policiesData && inCountData)`):
    // with no coverage payload — non-Pro adviser, or the dashboard fetch has
    // not landed — the list is left untouched rather than emptied.
    if (!users) return clients;
    const withoutCoverage = new Set(users.map(user => user._id));
    return clients.filter(client => withoutCoverage.has(client._id));
  }

  if (request.state === 'Disconnected') {
    return clients.filter(client => disconnectedFromAdviser(client, input.adviserEmail));
  }

  if (request.state === 'Inactive') {
    return clients.filter(client => Boolean(client.inactiveUser));
  }

  if (request.state === 'Pending') {
    return clients.filter(isPendingClient);
  }

  if (request.state === 'lucky') {
    // clientPage.js:1642-1650 narrows the list to the single lucky client. V1
    // reads it from its own `selectedLuckyClient` state; the request carries
    // the id instead, so the dashboard's own choice is honoured rather than a
    // stale one. With no id, leave the list alone — V1 renders an empty list
    // there, which is the bug this state exists to avoid reproducing.
    return filterByClientId(clients, request.clientId);
  }

  if (request.state === 'particular') {
    // clientPage.js:1246 — _.intersectionWith on preSelectedClientIds. With no
    // ids the intersection is empty, which is also what V1 renders.
    const ids = new Set(request.clientIds || []);
    return clients.filter(client => ids.has(client._id));
  }

  if (request.state === 'Long time no see') {
    // clientPage.js:1769 — connected-to-this-adviser (or inactive) AND a
    // latestMeetingDate on file, oldest meeting first. The qualification is the
    // same rule qualifyingFactsheetOf encodes, so read through it.
    const withMeeting = clients.filter(client =>
      Boolean(qualifyingFactsheetOf(client, input.adviserEmail)?.latestMeetingDate)
    );
    return _.sortBy(withMeeting, client => qualifyingFactsheetOf(client, input.adviserEmail)?.latestMeetingDate);
  }

  if (request.state === 'remindMe') {
    // clientPage.js:1805 — isClientRemindMe + remindMeSort, both from
    // utils/adviser-client.js (lodash + moment only, safe to import).
    return remindMeSort(clients.filter(client => isClientRemindMe(client, input.adviserEmail))) as QueueClient[];
  }

  if (request.state === 'dateBackBirthdayCount') {
    // clientPage.js:1720 — restrict to the dashboard's PastBirthdayOfUsers, then
    // order by MM/DD within two buckets (Jan-Feb, and Mar onwards). V1 puts the
    // Jan-Feb bucket first EXCEPT while the current month is itself Jan or Feb.
    const pastIds = new Set((input.pastBirthdayUsers || []).map(user => user._id));
    const dobOf = (client: QueueClient) => adviserFactsheetOf(client, input.adviserEmail)?.dob;
    const monthDay = (client: QueueClient) => moment.parseZone(dobOf(client)).format('MM/DD');
    const inPast = clients.filter(client => pastIds.has(client._id));
    // parseZone, not moment(), so the bucket and the MM/DD sort key below read
    // the same month: a dob carrying an explicit offset would otherwise shift a
    // month under local-time conversion and land in the wrong bucket.
    const dobMonth = (client: QueueClient) => moment.parseZone(dobOf(client)).month();
    const earlyMonths = _.sortBy(
      inPast.filter(client => dobMonth(client) < 2),
      monthDay
    );
    const laterMonths = _.sortBy(
      inPast.filter(client => dobMonth(client) >= 2),
      monthDay
    );
    const currentMonth = moment(input.now).month();
    const earlyFirst = earlyMonths.length > 0 && currentMonth >= 2;
    return earlyFirst ? [...earlyMonths, ...laterMonths] : [...laterMonths, ...earlyMonths];
  }

  if (request.state === 'longPendingCases') {
    // clientPage.js:2050 — tracking started more than TRACKING_MAX_DAYS ago,
    // measured fractionally as V1 does, then ordered by trackingSort.
    const now = moment(input.now);
    return trackingSort(
      clients.filter(client => {
        const startDate = client.trackingId?.startDate;
        return Boolean(startDate) && now.diff(moment(startDate), 'days', true) > TRACKING_MAX_DAYS;
      })
    ) as QueueClient[];
  }

  if (request.state === 'imported') {
    return clients.filter(client => Boolean(client.factsheetId?.isImported));
  }

  if (request.state === 'TrackOn') {
    // V1 reads the root trackingId here (not the per-adviser one) and orders by
    // trackingId.createdDate via trackingSort — utils/adviser-client.js only
    // pulls lodash+moment, so it is safe to reuse directly.
    return trackingSort(clients.filter(client => Boolean(client.trackingId?.isActive))) as QueueClient[];
  }

  if (request.state === 'Connected' || request.state === 'ClientToSend') {
    return filterByClientId(
      clients.filter(client => connectedToAdviser(client, input.adviserEmail)),
      request.clientId
    );
  }

  if (request.state === 'allReminder') {
    const reminderClients = filterByClientId(
      clients.filter(client => {
        const factsheet = qualifyingFactsheetOf(client, input.adviserEmail);
        return Boolean(factsheet?.fsReminderActive && factsheet.fsReminderDate);
      }),
      request.clientId
    );
    return [...reminderClients].sort((left, right) => {
      const leftDate = qualifyingFactsheetOf(left, input.adviserEmail)?.fsReminderDate;
      const rightDate = qualifyingFactsheetOf(right, input.adviserEmail)?.fsReminderDate;
      const dateOrder = moment(leftDate).valueOf() - moment(rightDate).valueOf();
      return dateOrder !== 0 ? dateOrder : byName(left, right);
    });
  }

  const now = moment(request.referenceDate === undefined ? input.now : request.referenceDate);
  const today = now.clone().startOf('day');
  const tomorrow = today.clone().add(1, 'day');
  return clients
    .filter(client => {
      // TodayAndTomorrowBirthday is one of the three list states V1 really does
      // gate on the connection (`clientPage.js:1988` — `x.connection &&
      // word.factsheetId.dob`), unlike the birthday STAT tile beside it. Read
      // through the gated resolver, not the adviser-owned one.
      const dob = qualifyingFactsheetOf(client, input.adviserEmail)?.dob;
      if (!dob) return false;
      const parsed = moment.parseZone(dob);
      if (!parsed.isValid()) return false;
      const month = parsed.month();
      const date = parsed.date();
      return (
        (month === today.month() && date === today.date()) || (month === tomorrow.month() && date === tomorrow.date())
      );
    })
    .sort((left, right) => {
      const birthdayOrder =
        birthdayKeyOf(left, input.adviserEmail, today) - birthdayKeyOf(right, input.adviserEmail, today);
      return birthdayOrder !== 0 ? birthdayOrder : byName(left, right);
    });
};
